// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import ChevronDown from "@eisinc/XDL/Icons/ChevronDown";
import { color } from "@eisinc/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Clickable from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { IconMemo, IconProps } from "@insite/mobius/Icon";
import ChevronLeft from "@insite/mobius/Icons/ChevronLeft";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { createRef, Ref, useCallback, useEffect, useImperativeHandle, useState } from "react";
import { Transition } from "react-transition-group";
import { TransitionStatus } from "react-transition-group/Transition";
import styled, { css, ThemeProps } from "styled-components";

export interface FancyScrollRefProps {
    updateScrollable: () => void;
}

export interface FancyScrollProps {
    children: React.ReactElement;
    refElement: React.RefObject<HTMLElement | null>;
    wrapperRef?: React.RefObject<HTMLElement>;
    showArrow?: boolean;
    extendedStyles?: InjectableCss;
    color?: string;
    scrollByValue?: number;
    shadowHeight?: string;
    isBoxShadow?: boolean;
    paddingRight?: number;
    contentPaddingRight?: number;
    extendToFullWidth?: boolean;
}

export interface FancyScrollStyle {
    showArrowWrapper?: InjectableCss;
    showArrowIcon?: IconProps;
}

export const fancyScrollStyle: FancyScrollStyle = {
    showArrowWrapper: {
        css: css`
            border: 1px solid ${getColor("common.border")};
            border-radius: 4px;
            background-color: ${getColor("common.white")};
            height: 24px;
            margin: 8px;
            width: 100%;
        `,
    },
    showArrowIcon: {
        size: 11,
        color: "text.link",
        css: css`
            margin: auto;
            display: block;
        `,
    },
};
const styles = fancyScrollStyle;

export const ScrollIndicator = styled.span<
    {
        direction: "up" | "down" | "left" | "right";
        transitionState: TransitionStatus;
        color?: string;
        shadowHeight?: string;
        extendToFullWidth?: boolean;
    } & ThemeProps<BaseTheme>
>`
    position: absolute;
    width: ${({ extendToFullWidth }) => (extendToFullWidth ? "100%" : " calc(100% - 8px)")};
    height: ${({ shadowHeight }) => shadowHeight};
    z-index: 1;
    color: ${getColor("common.ui1")};
    display: flex;
    align-items: end;
    justify-content: center;
    transition: all ease-in-out 250ms;
    pointer-events: none;
    ${({ transitionState }) => {
        switch (transitionState) {
            case "entering":
                return "opacity: 0;";
            case "entered":
                return "opacity: 1;";
            case "exiting":
            case "exited":
                return "opacity: 0;";
            default:
                return "";
        }
    }}
    ${({ direction, color: customColor, shadowHeight = "21px" }) => {
        if (direction === "up") {
            return css`
                top: 0;
                align-items: start;
                background: linear-gradient(
                    180deg,
                    ${customColor ? color(customColor, 1) : color("common.white", 1)} 0%,
                    ${customColor ? color(customColor, 0) : color("common.white", 0)} 100%
                );
            `;
        }
        if (direction === "down") {
            return css`
                bottom: 0;
                background: linear-gradient(
                    0deg,
                    ${customColor ? color(customColor, 1) : customColor || color("common.white", 1)} 0%,
                    ${customColor ? color(customColor, 0) : customColor || color("common.white", 0)} 100%
                );
            `;
        }
        if (direction === "left") {
            return css`
                left: 0;
                top: 0;
                width: ${shadowHeight};
                height: calc(100% - 8px);
                & ~ button > span[direction="down"] {
                    bottom: 8px; /* scroll x width in case we have it */
                }
                background: linear-gradient(
                    90deg,
                    ${customColor ? color(customColor, 1) : customColor || color("common.white", 1)} 0%,
                    ${customColor ? color(customColor, 0) : customColor || color("common.white", 0)} 100%
                );
            `;
        }
        if (direction === "right") {
            return css`
                right: 0;
                top: 0;
                width: ${shadowHeight};
                height: calc(100% - 8px);
                & ~ button > span[direction="down"] {
                    bottom: 8px; /* scroll x width in case we have it */
                }
                &:has(~ button > span[direction="up"]),
                &:has(~ button > span[direction="down"]) {
                    right: 8px; /* scroll y exist */
                }
                background: linear-gradient(
                    270deg,
                    ${customColor ? color(customColor, 1) : customColor || color("common.white", 1)} 0%,
                    ${customColor ? color(customColor, 0) : customColor || color("common.white", 0)} 100%
                );
            `;
        }
    }}
`;

const sum = (...a: number[]) => Math.ceil(a.reduce((previousValue, currentValue) => previousValue + currentValue, 0));

const FancyScroll = (
    {
        children,
        refElement,
        wrapperRef,
        showArrow,
        extendedStyles,
        color,
        scrollByValue,
        shadowHeight = "21px",
        isBoxShadow = false,
        paddingRight,
        contentPaddingRight,
        extendToFullWidth,
    }: FancyScrollProps,
    ref: Ref<{ updateScrollable: () => void }>,
) => {
    const [showScrollDown, setShowScrollDown] = useState(false);
    const [showScrollUp, setShowScrollUp] = useState(false);
    const [showScrollRight, setShowScrollRight] = useState(false);
    const [showScrollLeft, setShowScrollLeft] = useState(false);
    const scrollUpRef = createRef<any>();
    const scrollDownRef = createRef<any>();
    const scrollLeftRef = createRef<any>();
    const scrollRightRef = createRef<any>();

    useImperativeHandle(ref, () => ({
        updateScrollable() {
            setTimeout(() => checkScrollable());
        },
    }));
    const updateParentPadding = ({ hasVScroll }: { hasVScroll?: boolean } = {}) => {
        // if we need to stretch the body to remove padding
        if (hasVScroll) {
            if (wrapperRef?.current && paddingRight !== undefined) {
                wrapperRef.current.style.paddingRight = `${paddingRight}px`;
            }
            if (refElement.current && contentPaddingRight !== undefined) {
                refElement.current.style.paddingRight = `${contentPaddingRight}px`;
            }
        } else {
            if (wrapperRef?.current && paddingRight !== undefined) {
                wrapperRef.current.style.removeProperty("padding-right");
            }
            if (refElement.current && contentPaddingRight !== undefined) {
                refElement.current.style.removeProperty("padding-right");
            }
        }
    };
    const checkScrollable = useCallback(() => {
        if (refElement.current) {
            const hasVScroll = refElement.current.scrollHeight > refElement.current.clientHeight;
            updateParentPadding({ hasVScroll });
            if (
                refElement.current.scrollHeight > refElement.current.offsetHeight &&
                refElement.current.scrollTop > 30
            ) {
                setShowScrollUp(true);
            } else {
                setShowScrollUp(false);
            }
            if (refElement.current.scrollHeight > sum(refElement.current.offsetHeight + refElement.current.scrollTop)) {
                setShowScrollDown(true);
            } else {
                setShowScrollDown(false);
            }

            if (isBoxShadow) {
                // In case we need x scrolling shadow
                if (refElement.current.scrollLeft > 30) {
                    setShowScrollLeft(true);
                } else {
                    setShowScrollLeft(false);
                }
                if (refElement.current.scrollWidth > refElement.current.scrollLeft + refElement.current.offsetWidth) {
                    setShowScrollRight(true);
                } else {
                    setShowScrollRight(false);
                }
            }
        }
    }, [refElement]);

    useEffect(() => {
        if (refElement && refElement.current) {
            setTimeout(() => checkScrollable());
            refElement.current.addEventListener("scroll", checkScrollable, { passive: true });
        }
        return () => {
            if (refElement && refElement.current) {
                refElement.current.removeEventListener("scroll", checkScrollable);
            }
        };
    }, [refElement]);

    return (
        <StyledWrapper
            css={css`
                position: relative;
                ${extendedStyles?.css}
            `}
        >
            <Transition
                nodeRef={scrollLeftRef}
                mountOnEnter
                unmountOnExit
                in={showScrollLeft}
                timeout={{ enter: 75, exit: 250 }}
            >
                {state => (
                    <ScrollIndicator
                        direction="left"
                        transitionState={state}
                        color={color}
                        shadowHeight={shadowHeight}
                        extendToFullWidth={extendToFullWidth}
                        ref={scrollLeftRef}
                    >
                        {showArrow && <IconMemo src={ChevronLeft} size={14} />}
                    </ScrollIndicator>
                )}
            </Transition>
            <Transition
                nodeRef={scrollRightRef}
                mountOnEnter
                unmountOnExit
                in={showScrollRight}
                timeout={{ enter: 75, exit: 250 }}
            >
                {state => (
                    <ScrollIndicator
                        direction="right"
                        transitionState={state}
                        color={color}
                        shadowHeight={shadowHeight}
                        extendToFullWidth={extendToFullWidth}
                        ref={scrollRightRef}
                    >
                        {showArrow && <IconMemo src={ChevronRight} size={14} />}
                    </ScrollIndicator>
                )}
            </Transition>
            <Transition
                mountOnEnter
                unmountOnExit
                in={showScrollUp}
                nodeRef={scrollUpRef}
                timeout={{ enter: 75, exit: 250 }}
            >
                {state => (
                    <Clickable
                        onClick={() => {
                            refElement.current?.scrollBy({
                                top: scrollByValue ? -scrollByValue : 0,
                                behavior: "smooth",
                            });
                        }}
                        aria-label="arrow up"
                    >
                        <ScrollIndicator
                            ref={scrollUpRef}
                            direction="up"
                            transitionState={state}
                            color={color}
                            shadowHeight={shadowHeight}
                            extendToFullWidth={extendToFullWidth}
                        >
                            {showArrow && <IconMemo src={ChevronDown} size={11} />}
                        </ScrollIndicator>
                    </Clickable>
                )}
            </Transition>
            {children}
            <Transition
                nodeRef={scrollDownRef}
                mountOnEnter
                unmountOnExit
                in={showScrollDown}
                timeout={{ enter: 75, exit: 250 }}
            >
                {state => (
                    <Clickable
                        onClick={() => {
                            refElement.current?.scrollBy({
                                top: scrollByValue ?? 0,
                                behavior: "smooth",
                            });
                        }}
                        aria-label="arrow down"
                    >
                        <ScrollIndicator
                            ref={scrollDownRef}
                            direction="down"
                            transitionState={state}
                            color={color}
                            shadowHeight={shadowHeight}
                            extendToFullWidth={extendToFullWidth}
                        >
                            {showArrow && <IconMemo src={ChevronDown} size={11} />}
                        </ScrollIndicator>
                    </Clickable>
                )}
            </Transition>
        </StyledWrapper>
    );
};

export default React.forwardRef(FancyScroll);
